import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ImageIcon from "@mui/icons-material/Image";
import SendIcon from "@mui/icons-material/Send";
import MenuItem from "@mui/material/MenuItem";

const NotificationModal = ({
  open,
  onClose,
  isBulk,
  notifyForm,
  setNotifyForm,
  notifyErrors,
  setNotifyErrors,
  onSend,
  countryList = [],
}) => {
  const updateButton = (index, field, value) => {
    setNotifyForm((prev) => ({
      ...prev,
      buttons: prev.buttons.map((b, i) =>
        i === index ? { ...b, [field]: value } : b
      ),
    }));
    if (notifyErrors.buttons) {
      setNotifyErrors((prev) => ({ ...prev, buttons: "" }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth PaperProps={{
      sx: {
        maxWidth: "900px",
      },
    }}>
      {/* HEADER */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          backgroundColor: "#f5f7fa",
          fontWeight: 600,
        }}
      >
        <NotificationsActiveIcon color="primary" />
        {isBulk ? "Bulk Push Notification" : "Push Notification"}
      </DialogTitle>

      {/* BODY */}
      <DialogContent sx={{ pt: 2 }}>
        {/* Country filter for bulk */}
        {isBulk && (
          <TextField
            select
            fullWidth
            label="Country (optional)"
            margin="dense"
            value={notifyForm.country || ""}
            error={Boolean(notifyErrors.country)}
            helperText={
              notifyErrors.country ||
              "Select a country to target, or leave empty for all countries"
            }
            onChange={(e) => {
              setNotifyForm({ ...notifyForm, country: e.target.value });
              setNotifyErrors({ ...notifyErrors, country: "" });
            }}
            sx={{ mb: 1 }}
            SelectProps={{
              renderValue: (v) =>
                v === "" ? "" : v === "all" ? "All Countries" : v,
              displayEmpty: true,
              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: 48 * 6,
                  },
                },
              },
            }}
          >
            <MenuItem value="">
              <em>All Countries</em>
            </MenuItem>
            {countryList.map((item, index) => (
              <MenuItem key={index} value={item.countryCode}>
                {item.countryCode}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* Title */}
        <TextField
          fullWidth
          label="Notification Title"
          placeholder="e.g. Big Sale is Live!"
          margin="dense"
          value={notifyForm.title}
          error={Boolean(notifyErrors.title)}
          helperText={notifyErrors.title}
          onChange={(e) => {
            setNotifyForm({ ...notifyForm, title: e.target.value });
            setNotifyErrors({ ...notifyErrors, title: "" });
          }}
          sx={{ mt: 2 }}
        />

        {/* Body */}
        <TextField
          fullWidth
          label="Notification Message"
          placeholder="Write your message here..."
          margin="dense"
          multiline
          rows={4}
          value={notifyForm.body}
          error={Boolean(notifyErrors.body)}
          helperText={notifyErrors.body}
          onChange={(e) => {
            setNotifyForm({ ...notifyForm, body: e.target.value });
            setNotifyErrors({ ...notifyErrors, body: "" });
          }}
          sx={{ mt: 2 }}
        />

        {/* Image URL */}
        <TextField
          fullWidth
          label="Image URL"
          placeholder="https://example.com/image.png"
          margin="dense"
          value={notifyForm.image_url}
          error={Boolean(notifyErrors.image_url)}
          helperText={notifyErrors.image_url}
          onChange={(e) => {
            setNotifyForm({ ...notifyForm, image_url: e.target.value });
            setNotifyErrors({ ...notifyErrors, image_url: "" });
          }}
          sx={{ mt: 2 }}
          InputProps={{
            startAdornment: <ImageIcon sx={{ mr: 1, color: "gray" }} />,
          }}
        />

        {/* IMAGE PREVIEW */}
        {notifyForm.image_url && !notifyErrors.image_url && (
          <Box
            sx={{
              mt: 2,
              borderRadius: 2,
              border: "1px solid #e0e0e0",
              backgroundColor: "#fafafa",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: 1,
            }}
          >
            <img
              src={notifyForm.image_url}
              alt="Preview"
              style={{
                width: "100%",
                maxHeight: 220,
                objectFit: "contain",
                borderRadius: 8,
              }}
              onError={(e) => (e.target.style.display = "none")}
            />
          </Box>
        )}

        {/* Link */}
        <TextField
          fullWidth
          label="Link"
          placeholder="Enter Link"
          margin="dense"
          value={notifyForm.link}
          error={Boolean(notifyErrors.link)}
          helperText={notifyErrors.link}
          onChange={(e) => {
            setNotifyForm({ ...notifyForm, link: e.target.value });
            setNotifyErrors({ ...notifyErrors, link: "" });
          }}
        />

        {/* Buttons (fixed id: btn_1, btn_2; label and link editable) */}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
          Action Buttons
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Two fixed buttons: Link + Discount Code
          </Typography>
        </Box>

        {(() => {
          const idx = notifyForm.buttons.findIndex((b) => b.id === "btn_1");
          const btn = idx >= 0 ? notifyForm.buttons[idx] : { id: "btn_1", label: "", link: "" };
          return (
            <Box
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "#fff",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    Button 1 (Link)
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "#fafafa",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {btn.id}
                </Box>
              </Box>

              <TextField
                fullWidth
                size="small"
                label="Button Label"
                placeholder="e.g. Shop Now"
                margin="dense"
                value={btn.label}
                onChange={(e) => updateButton(idx, "label", e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                label="Button Link"
                placeholder="e.g. https://sareesbazaar.in/collections/readymade"
                margin="dense"
                value={btn.link || ""}
                error={Boolean(notifyErrors.buttons)}
                onChange={(e) => updateButton(idx, "link", e.target.value)}
              />
            </Box>
          );
        })()}

        {(() => {
          const idx = notifyForm.buttons.findIndex((b) => b.id === "btn_2");
          const btn = idx >= 0 ? notifyForm.buttons[idx] : { id: "btn_2", label: "", discountCode: "" };
          return (
            <Box
              sx={{
                p: 2,
                mb: 1,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "#fff",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    Button 2 (Discount Code)
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "#fafafa",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {btn.id}
                </Box>
              </Box>

              <TextField
                fullWidth
                size="small"
                label="Discount Label"
                placeholder="e.g. Copy Code"
                margin="dense"
                value={btn.label}
                onChange={(e) => updateButton(idx, "label", e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                label="Discount Code"
                placeholder="e.g. 10%OFF"
                margin="dense"
                value={btn.discountCode || ""}
                error={Boolean(notifyErrors.buttons)}
                onChange={(e) => updateButton(idx, "discountCode", e.target.value)}
              />
            </Box>
          );
        })()}
        {notifyErrors.buttons && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mt: -0.5, mb: 1 }}
          >
            {notifyErrors.buttons}
          </Typography>
        )}
      </DialogContent>

      {/* FOOTER */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          backgroundColor: "#fafafa",
        }}
      >
        <Button onClick={onClose}>Cancel</Button>

        <Button
          variant="contained"
          endIcon={<SendIcon />}
          onClick={onSend}
          sx={{
            borderRadius: "8px",
            px: 3,
            fontWeight: 600,
            backgroundColor: "#1976d2",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
          }}
        >
          {isBulk ? "Send to All" : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationModal;

